import type {
  ExplainExplanation,
  QueryDslQueryContainer,
  SearchRescore,
} from '@elastic/elasticsearch/lib/api/types'

import type OpenAI from 'openai'
import type {
  NonArray,
  SearchHitWithRequiredFields,
  SearchObjectNoteFragment,
} from './mapping'
import type { GPTMessage, NER } from './playground'
import {
  SuperHit,
  SuperStreamEventSearchFilters,
  SuperStreamEventSourceExplanation,
  SuperStreamEventToolExplanationText,
  SuperStreamEventSources,
  SuperStreamEventTokenConsumption,
  ESParams,
  SuperStreamEventArtifacts,
  SuperStreamEventBuildingAnalysis,
  SuperStreamEventGeneric,
  SuperStreamEventLinear,
  SuperStreamEventGithub,
  SuperStreamEventJira,
  SuperStreamEventFailingTool,
  isSupportedSuperToolChainEventId,
} from './super'
import { ParsedEvent, ReconnectInterval } from 'eventsource-parser'

export enum AskStreamEventId {
  Start = 'start',
  End = 'end',

  TokenConsumption = 'token-consumption',
  SuperLinear = 'askx-linear',
  SuperAsana = 'askx-asana',
  SuperGithub = 'askx-github',
  SuperJira = 'askx-jira',
  SuperSourceExplanation = 'askx-source-explanation',
  SuperSources = 'askx-sources',
  SuperToolExplanationText = 'askx-tool-explanation',
  SuperToolAggregationText = 'askx-tool-aggregation',
  Sources = 'sources',
  OriginalSources = 'original-sources',
  SelectedSources = 'selected-sources',
  SourceRange = 'source-range',
  SourceExplanation = 'source-explanation',
  SourceMedia = 'source-media',
  SuperArtifacts = 'super-artifacts',
  ThreadId = 'thread-id',
  Text = 'text',
  Thought = 'thought',
  Error = 'ask-error',
  NoAnswer = 'no-answer',
  BadAnswer = 'bad-answer',
  QuestionId = 'question-id',
  Debug = 'debug',
  DebugHits = 'debug-hits',
  DebugSql = 'debug-sql',
  DebugGPTMessages = 'debug-gpt-messages',
  DebugInferredQuestion = 'debug-inferred-question',
  DebugInferredPrecision = 'debug-inferred-precision',
  DebugInferredKeywords = 'debug-inferred-keywords',
  DebugInferredQuestionWithPrecision = 'debug-inferred-question-with-precision',
  DebugSuperQuery = 'debug-askx-query',
  DebugGraphEvents = 'debug-graph-events',
  InferredQuestion = 'inferred-question',
  InferredKeywords = 'inferred-keywords',
  InferredPrecision = 'inferred-precision',
  FollowUpQuestions = 'follow-up-questions',
  NEREntities = 'ner-entities',
  DatabaseSources = 'database-sources',
  SuggestedFilters = 'suggested-filters',
  SearchFilters = 'search-filters',
  BuildingSearchFilters = 'building-search-filters',
  BuildingAnalysis = 'building-analysis',
  Overloaded = 'overloaded',
  NeedPrecisions = 'need-precisions',
  RawDebug = 'raw-debug',
  FailingTool = 'failing-tool',

  // Iteration is not handled by ask, it should be removed
  Iteration = 'iteration',
}

export enum AskResult {
  Answered = 'answered',
  NoAnswer = 'no-answer',
}

export type AskStreamEventIteration = {
  choices: Array<{
    delta: OpenAI.ChatCompletionMessage
  }>
}

export type AskStreamEventText = {
  text: string
}

export type AskRequiredFields = Required<{
  [K in keyof Pick<
    SearchObjectNoteFragment,
    | 'fragmentFullText'
    | 'fullPath'
    | 'noteId'
    | 'title'
    | 'fragmentTitle'
    | 'fragmentSemantic'
    | 'fragmentMarkdown'
    | 'fragmentId'
    | 'type'
    | 'fragmentRange'
    | 'updatedAt'
    | 'lastEditedAt'
    | 'channelPurpose'
    | 'fragmentDatabase'
    | 'fragmentVectorBGE'
    | 'createdAt'
    | 'fragmentIndex'
  >]: NonNullable<NonArray<SearchObjectNoteFragment[K]>>
}> & {
  [K in keyof Pick<
    SearchObjectNoteFragment,
    | 'iconShape'
    | 'iconColor'
    | 'iconVariant'
    | 'reviewState'
    | 'externalType'
    | 'externalUrl'
    | 'fragmentType'
  >]: NonNullable<NonArray<SearchObjectNoteFragment[K]>>
} & Required<{
    [K in keyof Pick<
      SearchObjectNoteFragment,
      'parentNotesIdList' | 'parentNotesTitleList'
    >]: NonNullable<NonArray<SearchObjectNoteFragment[K]>>[]
  }>

// Only used to have type safety and ASK_REQUIRED_FIELDS matching AskRequiredFields type definition
const askRequiredFieldsHelper: {
  [K in keyof Required<AskRequiredFields>]: true
} = {
  fragmentTitle: true,
  fragmentIndex: true,
  fragmentFullText: true,
  fragmentSemantic: true,
  fragmentMarkdown: true,
  fragmentRange: true,
  fragmentId: true,
  fullPath: true,
  noteId: true,
  title: true,
  type: true,
  updatedAt: true,
  lastEditedAt: true,
  iconColor: true,
  iconShape: true,
  iconVariant: true,
  reviewState: true,
  parentNotesIdList: true,
  parentNotesTitleList: true,
  channelPurpose: true,
  externalType: true,
  externalUrl: true,
  fragmentType: true,
  fragmentDatabase: true,
  fragmentVectorBGE: true,
  createdAt: true,
}

export const ASK_REQUIRED_FIELDS = Object.freeze(
  Object.keys(askRequiredFieldsHelper)
)

export type AskSource = {
  score: number
  scoreBeforeDecay: number
  answerSimilarityScore: number
  explanation?: ExplainExplanation
  fragmentRange?: [number[], number[]]
  parentNotes: { id: string; title: string }[]
} & Omit<
  AskRequiredFields,
  | 'fragmentFullText'
  | 'fragmentMarkdown'
  | 'fragmentDatabase'
  | 'fragmentSemantic'
  | 'fragmentRange'
  | 'parentNotesTitleList'
  | 'parentNotesIdList'
  | 'fragmentVectorBGE'
> &
  Partial<
    Pick<
      AskRequiredFields,
      | 'fragmentFullText'
      | 'fragmentMarkdown'
      | 'fragmentSemantic'
      | 'fragmentType'
      | 'fragmentVectorBGE'
    >
  >

export type UserAsksResponse = {
  answer: string
  sources: AskSource[]
}

export type AskStreamEventSources = {
  sources: UserAsksResponse['sources']
}

// Copied from collab
export enum ColumnTypes {
  COLLECTION_NOTE_TITLE = 'collection-note-title',
  CREATED_AT = 'created-at',
  CREATED_BY = 'created-by',
  DATE = 'date',
  MULTI_SELECT = 'multi-select',
  NAME = 'name',
  NOTES = 'notes',
  NUMBER = 'number',
  PRIMARY = 'primary',
  RICH_TEXT = 'rich-text',
  SELECT = 'select',
  TEXT = 'text',
  // TODO: remove once our internal tables get off deprecated "grid"
  GRID = 'grid',
  TODO = 'todo',
  UPDATED_AT = 'updated-at',
  UPDATED_BY = 'updated-by',
  USER = 'user',
  USERS = 'users',
  LINK = 'link',
}

export type NoteDatabaseFragment = {
  blockId: string
  title: string
  range: [number[], number[]]
  columns: Record<
    string,
    { type: ColumnTypes; name: string; options?: string[] }
  >
  data: { key: string; fields: Record<string, string> }[]
}

export enum DatabaseQuestionResultType {
  Filtered = 'Filtered',
  Reduced = 'Reduced',
  Grouped = 'Grouped',
  Misc = 'Misc',
}

export type FilteredDatabase = {
  type: DatabaseQuestionResultType.Filtered
  result: {
    data: NoteDatabaseFragment['data']
    columns: NoteDatabaseFragment['columns']
  }
}

export type ReducedDatabase = {
  type: DatabaseQuestionResultType.Reduced
  result: string | number | boolean
}

export type GroupedDatabase = {
  type: DatabaseQuestionResultType.Grouped
  result: Record<string, number>
}

export type MiscDatabase = {
  type: DatabaseQuestionResultType.Misc
  result: unknown[]
}

export type DatabaseQuestionResult = { query?: string } & (
  | FilteredDatabase
  | ReducedDatabase
  | GroupedDatabase
  | MiscDatabase
)

export type AskStreamEventDatabaseSources = {
  sources: (AskSource & { questionResult?: DatabaseQuestionResult })[]
}

export type AskStreamEventSourceRange = {
  fragmentId: AskSource['fragmentId']
  noteId: AskSource['noteId']
  range: [number, number]
  answerSimilarityScore: number
}

export type AskStreamEventSourceExplanation = {
  fragmentId: AskSource['fragmentId']
  noteId: AskSource['noteId']
  explanation: string
}

export type AskStreamEventSourceMedia = {
  fragmentId: AskSource['fragmentId']
  noteId: AskSource['noteId']
  media: { id: string; name: string }
}

export type AskStreamEventSuggestedFilters = {
  parentNotesList: { id: string; title: string }[][]
  ranges: { start: Date; end: Date }[]
}

export type AskStreamEventNoAnswer = {
  totalWords: number
}

export enum OpenAIGPTEngine {
  ChatGPT4 = 'chatGPT4',
  ChatGPT4_O = 'chatGPT4o',
  ChatGPT4OMini = 'chatGPT4oMini',
}

export enum CustomGPTEngine {
  SelfHosted1 = 'selfHosted1',
}

export const GPTEngine = {
  ...OpenAIGPTEngine,
  ...CustomGPTEngine,
} as const

export type GPTEngine = (typeof GPTEngine)[keyof typeof GPTEngine]

export type AskStreamEventInferredQuestion = {
  inferredQuestion: string
  semanticQueries?: string[]
  tfIdfQueries?: string[]
}

export type AskStreamEventInferredKeywords = {
  inferredKeywords: string[]
}

export type AskStreamEventInferredPrecision = {
  version: string
  precision: string | null
  suggestions?: string[]
}

export type AskStreamEventFollowUpQuestion = {
  version: string
  questions?: string[]
}

export type AskStreamEventNEREntities = {
  entities: NER[]
}

export type AskStreamEventQuestionId = {
  questionId: string
}

export type AskStreamEventDebug = {
  rawSources: (SearchHitWithRequiredFields<AskRequiredFields> & {
    score?: number
    scoreBeforeDecay?: number
  })[]
  query: QueryDslQueryContainer
  rescore: SearchRescore | null
  lang?: string
  question?: string
  questionVector: number[]
}

export type AskStreamEventDebugHits = {
  rawSources: (Omit<SuperHit, 'relativeId'> & {
    score?: number
    scoreBeforeDecay?: number
  })[]
  query: QueryDslQueryContainer
  rescore: SearchRescore | null
  lang?: string
  question?: string
  questionVector: number[]
}

export type AskStreamEventDebugSql = {
  sqlCreation: string
  description: string
  sql?: string
  answerTemplate?: string
  question: string
  databaseFragment: SearchHitWithRequiredFields<AskRequiredFields>
  answer: string
  sqlResult: unknown[]
}

export enum AnswerProblemType {
  Prompt = 'prompt',
  FirstRetrieval = 'first_retrieval',
  ReRanking = 're_ranking',
  SqlOnDatabase = 'sql_on_database',
  None = 'none',
  Ignore = 'ignore',
}

export type AskTestCase = {
  // Potentially original answered returned (saved with debug data)
  reason?: string
  retrieval: AskStreamEventDebug
  sql: AskStreamEventDebugSql[]
  gptMessages: GPTMessage[]
  expectedFacts?: string[]
  expectedRubrics?: string[]
  currentDate?: Date
  lang: string
  messages?: GPTMessage[]
  question?: string // deprecated, we now use all the messages

  inferredPrecision?: GPTMessage[]
  followUpQuestions?: GPTMessage[]
  answerProblemType?: AnswerProblemType
  isHallucination?: boolean
  isDataMissing?: boolean
  shouldHavePrecision?: boolean
  shouldHaveAnswer?: boolean
}

export type BGEWeights = {
  dense?: number
  sparse?: number
  colbert?: number
}

export function isSupportedAskEventId(
  askEventId: string | undefined
): askEventId is AskStreamEventId {
  return Boolean(
    askEventId &&
      Object.values(AskStreamEventId)
        .map((a) => a.toString())
        .includes(askEventId)
  )
}

export type AskStreamEvent =
  | { id: AskStreamEventId.Start; event: object }
  | { id: AskStreamEventId.End; event: object }
  | { id: AskStreamEventId.Sources; event: AskStreamEventSources }
  | { id: AskStreamEventId.OriginalSources; event: AskStreamEventSources }
  | { id: AskStreamEventId.SelectedSources; event: AskStreamEventSources }
  | { id: AskStreamEventId.SourceRange; event: AskStreamEventSourceRange }
  | {
      id: AskStreamEventId.SourceExplanation
      event: AskStreamEventSourceExplanation
    }
  | { id: AskStreamEventId.SourceMedia; event: AskStreamEventSourceMedia }
  | {
      id: AskStreamEventId.SuggestedFilters
      event: AskStreamEventSuggestedFilters
    }
  | { id: AskStreamEventId.NoAnswer; event: AskStreamEventNoAnswer }
  | { id: AskStreamEventId.BadAnswer; event?: string }
  | { id: AskStreamEventId.QuestionId; event: AskStreamEventQuestionId }
  | { id: AskStreamEventId.Debug; event: AskStreamEventDebug }
  | { id: AskStreamEventId.DebugSql; event: AskStreamEventDebugSql }
  | {
      id: AskStreamEventId.DebugGPTMessages
      event: GPTMessage[]
    }
  | {
      id: AskStreamEventId.DebugInferredQuestion
      event: GPTMessage[]
    }
  | {
      id: AskStreamEventId.DebugInferredKeywords
      event: GPTMessage[]
    }
  | {
      id: AskStreamEventId.DebugInferredQuestionWithPrecision
      event: GPTMessage[]
    }
  | {
      id: AskStreamEventId.DebugInferredPrecision
      event: GPTMessage[]
    }
  | { id: AskStreamEventId.DebugGraphEvents; event: Record<string, unknown>[] }
  // We should remove this one
  | { id: AskStreamEventId.Iteration; event: AskStreamEventIteration }
  | {
      id: AskStreamEventId.InferredQuestion
      event: AskStreamEventInferredQuestion
    }
  | {
      id: AskStreamEventId.InferredKeywords
      event: AskStreamEventInferredKeywords
    }
  | {
      id: AskStreamEventId.InferredPrecision
      event: AskStreamEventInferredPrecision
    }
  | {
      id: AskStreamEventId.FollowUpQuestions
      event: AskStreamEventFollowUpQuestion
    }
  | { id: AskStreamEventId.NEREntities; event: AskStreamEventNEREntities }
  | {
      id: AskStreamEventId.DatabaseSources
      event: AskStreamEventDatabaseSources
    }
  | { id: AskStreamEventId.Text; event: { text: string; source?: string } }
  | { id: AskStreamEventId.Thought; event: { text: string } }
  | { id: AskStreamEventId.Error; event: { message?: string; name?: string } }
  // Specific to super
  | { id: AskStreamEventId.SuperSources; event: SuperStreamEventSources }
  | { id: AskStreamEventId.SuperArtifacts; event: SuperStreamEventArtifacts }
  | { id: AskStreamEventId.ThreadId; event: { threadId: string } }
  | { id: AskStreamEventId.DebugHits; event: AskStreamEventDebugHits }
  | {
      id: AskStreamEventId.SuperSourceExplanation
      event: SuperStreamEventSourceExplanation
    }
  | {
      id: AskStreamEventId.SuperLinear
      event: SuperStreamEventLinear
    }
  | {
      id: AskStreamEventId.SuperAsana
      event: SuperStreamEventGeneric
    }
  | {
      id: AskStreamEventId.SuperGithub
      event: SuperStreamEventGithub
    }
  | {
      id: AskStreamEventId.SuperJira
      event: SuperStreamEventJira
    }
  | {
      id: AskStreamEventId.SuperToolExplanationText
      event: SuperStreamEventToolExplanationText
    }
  | {
      id: AskStreamEventId.SuperToolAggregationText
      event: SuperStreamEventToolExplanationText
    }
  | {
      id: AskStreamEventId.DebugSuperQuery
      event: {
        query: ESParams
        semanticContent?: string
      }
    }
  | {
      id: AskStreamEventId.TokenConsumption
      event: SuperStreamEventTokenConsumption
    }
  | {
      id: AskStreamEventId.SearchFilters
      event: SuperStreamEventSearchFilters
    }
  | {
      id: AskStreamEventId.BuildingAnalysis
      event: SuperStreamEventBuildingAnalysis
    }
  | {
      id: AskStreamEventId.Overloaded
      event: { tries?: number; step?: string; fallback?: boolean }
    }
  | {
      id: AskStreamEventId.NeedPrecisions
      event: { question: string; choices: string[]; questionId: string }
    }
  | {
      id: AskStreamEventId.RawDebug
      event: unknown
    }
  | {
      id: AskStreamEventId.FailingTool
      event: SuperStreamEventFailingTool
    }

export function handleAskEvents(
  _: AskStreamEvent | undefined,
  onData: (askStreamEvent: AskStreamEvent) => void
) {
  return function handleSseEvent(parsedEvent: ParsedEvent | ReconnectInterval) {
    if (parsedEvent.type !== 'event') {
      return
    }
    const { event: eventId, data } = parsedEvent
    if (
      !(
        isSupportedAskEventId(eventId) ||
        isSupportedSuperToolChainEventId(eventId)
      )
    ) {
      return
    }
    const event = {
      id: eventId,
      event: JSON.parse(data),
    } as AskStreamEvent //TS complaining otherwise because union has 25+ members
    onData(event)
  }
}
